import React from 'react';
import { type MenuProps } from 'antd';
import { H } from './design-tokens';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { HeaderBrand } from './header-brand';
import { HeaderDesktopActions } from './header-desktop-actions';
import { HeaderMobileTrigger } from './header-mobile-trigger';
import { MobileDrawer } from './mobile-drawer';

export interface PlatformModule {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

export interface PlatformHeaderProps {
  clientLogo: React.ReactNode;
  modules: PlatformModule[];
  activeModule: string;
  navItems?: React.ReactNode;
  extraActions?: React.ReactNode;
  onModuleChange: (moduleId: string) => void;
  onSignOut: () => void;
  /** Mobile drawer: menu items for vertical nav */
  drawerMenuItems?: MenuProps['items'];
  /** Mobile drawer: active menu key */
  drawerActiveKey?: string;
  /** Mobile drawer: callback when a menu item is selected */
  onDrawerMenuSelect?: (key: string) => void;
  /** Mobile drawer: extra content below the menu (e.g. TreeView) */
  drawerExtraContent?: React.ReactNode;
  userName?: string;
  userEmail?: string;
  environment?: 'alt';
}

export const PlatformHeader: React.FC<PlatformHeaderProps> = ({
  clientLogo,
  modules,
  activeModule,
  navItems,
  extraActions,
  onModuleChange,
  onSignOut,
  drawerMenuItems,
  drawerActiveKey,
  onDrawerMenuSelect,
  drawerExtraContent,
  userName,
  userEmail,
  environment
}) => {
  const { spacing, screens } = useResponsiveSizes();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  React.useEffect(() => {
    if (screens.md && drawerOpen) setDrawerOpen(false);
  }, [screens.md, drawerOpen]);

  const sidePadding = `0 ${spacing.xxl}px`;

  return (
    <>
      <div
        style={{
          height: H.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: H.bg,
          color: H.textMuted,
          borderBottom: `1px solid ${H.border}`,
          position: 'relative',
          flexShrink: 0,
          zIndex: 30,
          boxSizing: 'border-box'
        }}
      >
        <div
          style={{
            display: 'flex',
            margin: sidePadding,
            alignItems: 'center',
            gap: spacing.sm,
            flexShrink: 0
          }}
        >
          <HeaderBrand environment={environment} />
          {clientLogo}
        </div>

        {screens.md ? (
          <HeaderDesktopActions
            navItems={navItems}
            extraActions={extraActions}
            modules={modules}
            activeModule={activeModule}
            onModuleChange={onModuleChange}
            onSignOut={onSignOut}
          />
        ) : (
          <HeaderMobileTrigger onClick={() => setDrawerOpen(true)} />
        )}
      </div>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userName={userName}
        userEmail={userEmail}
        menuItems={drawerMenuItems}
        activeKey={drawerActiveKey}
        onMenuSelect={onDrawerMenuSelect}
        modules={modules}
        activeModule={activeModule}
        onModuleChange={onModuleChange}
        onSignOut={onSignOut}
        extraContent={drawerExtraContent}
      />
    </>
  );
};
